import React, { createContext, useEffect, useState, useReducer } from "react";

export const ThemeContext = createContext();
const initialState = {
  background : { value: "light", label: "Light"},
};
const reducer = (previousState, updatedState) => ({
 ...previousState,
 ...updatedState,
});
const ThemeContextProvider = (props) => {
  const [, dispatch] = useReducer(reducer, initialState);

    const body = document.querySelector("body");
    const [windowWidth, setWindowWidth] = useState(0);
    const [windowHeight, setWindowHeight] = useState(0);
    const [layout, setLayout] = useState('vertical');
    const [themeMode, setThemeMode] = useState('light');

    const changeBackground = (name) => {
      body.setAttribute("data-theme-version", name.value);
      dispatch({background: name});
    };

    const changeLayout = (newLayout) => {
      // layout: 'vertical' | 'horizontal'
      body.setAttribute("data-layout", newLayout);
      setLayout(newLayout);
    };

    const changeTheme = (theme) => {
      // theme: 'light' | 'dark'
      body.setAttribute("data-theme-version", theme);
      setThemeMode(theme);
    };
	
    // Inject dark-theme form overrides directly into the document head so they
    // are guaranteed to be present regardless of CSS bundle load order.
    useEffect(() => {
      const el = document.createElement('style');
      el.id = 'praxxis-dark-form-overrides';
      /* Using #root gives us an ID selector which beats any number of class
         selectors in component-level <style> blocks that use !important       */
      /* body carries [data-theme-version="dark"], #root is inside body,
         so the correct descendant order is: body-attr > #root > element   */
      el.textContent = `
        [data-theme-version="dark"] #root select,
        [data-theme-version="dark"] #root .form-select {
          background-color: #1f1e26 !important;
          border-color: #343240 !important;
          color: #fff !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: right 0.75rem center !important;
          background-size: 16px 12px !important;
          padding-right: 2.25rem !important;
        }
        [data-theme-version="dark"] #root select option,
        [data-theme-version="dark"] #root .form-select option {
          background-color: #1f1e26 !important;
          color: #fff !important;
        }
        [data-theme-version="dark"] #root select optgroup,
        [data-theme-version="dark"] #root .form-select optgroup {
          background-color: #2c2b35 !important;
          color: #aaa !important;
        }

        /* React-Bootstrap modals are portal'd to document.body, so they
           render OUTSIDE #root. Per-page light-blue rules like
           ".X-form select.form-select:not(:disabled) { #e8f0fe !important; }"
           still apply inside modals. The doubled attribute selector raises
           our specificity above those (0,4,0,1 > 0,3,0,1) so dark mode wins. */
        [data-theme-version="dark"][data-theme-version="dark"] select.form-select:not(:disabled),
        [data-theme-version="dark"][data-theme-version="dark"] select:not(.form-select):not(:disabled),
        [data-theme-version="dark"][data-theme-version="dark"] .form-select:not(:disabled) {
          background-color: #1f1e26 !important;
          border-color: #343240 !important;
          color: #fff !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: right 0.75rem center !important;
          background-size: 16px 12px !important;
        }
        [data-theme-version="dark"][data-theme-version="dark"] select.form-select option,
        [data-theme-version="dark"][data-theme-version="dark"] select option,
        [data-theme-version="dark"][data-theme-version="dark"] .form-select option {
          background-color: #1f1e26 !important;
          color: #fff !important;
        }

        /* Focus state — thin teal border on all form controls in dark mode.
           Uses #17a2b8 (Bootstrap cyan) which is more blue-leaning than the
           greenish #20c997 used in light mode, so it reads as teal on the
           dark background rather than fluorescing green. Matches the body+#root
           and doubled-attribute specificity tiers so it overrides the gray
           border-color rules for in-#root and modal-portal'd selects alike. */
        [data-theme-version="dark"] #root select:focus,
        [data-theme-version="dark"] #root .form-select:focus,
        [data-theme-version="dark"] #root .form-control:focus,
        [data-theme-version="dark"] #root textarea.form-control:focus {
          border-color: #17a2b8 !important;
          box-shadow: none !important;
          outline: 0 !important;
        }
        [data-theme-version="dark"][data-theme-version="dark"] select.form-select:focus,
        [data-theme-version="dark"][data-theme-version="dark"] select:not(.form-select):focus,
        [data-theme-version="dark"][data-theme-version="dark"] .form-select:focus,
        [data-theme-version="dark"][data-theme-version="dark"] .form-control:focus,
        [data-theme-version="dark"][data-theme-version="dark"] textarea.form-control:focus {
          border-color: #17a2b8 !important;
          box-shadow: none !important;
          outline: 0 !important;
        }

        /* ── Cross-page dark-mode coverage ─────────────────────────────────
           These rules target patterns that recur on many pages and modals.
           They run at body-attribute specificity so portaled modals are
           covered too. Using the doubled attribute selector + !important
           beats per-page inline styles. */

        /* Patient/medication/CPT lookup dropdowns reused on many forms */
        body[data-theme-version="dark"] .pat-lookup-dropdown,
        body[data-theme-version="dark"] .med-name-dropdown,
        body[data-theme-version="dark"] .global-cpt-dropdown,
        body[data-theme-version="dark"] .ai-history-list,
        body[data-theme-version="dark"] .ai-example-chip {
          background-color: #2a2840 !important;
          border-color: #3a3852 !important;
          color: #d0d3e0 !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4) !important;
        }
        body[data-theme-version="dark"] .pat-lookup-result,
        body[data-theme-version="dark"] .med-name-dropdown-item,
        body[data-theme-version="dark"] .global-cpt-dropdown-item {
          background-color: transparent !important;
          color: #d0d3e0 !important;
          border-color: #3a3852 !important;
        }
        body[data-theme-version="dark"] .pat-lookup-result:hover,
        body[data-theme-version="dark"] .med-name-dropdown-item:hover,
        body[data-theme-version="dark"] .global-cpt-dropdown-item:hover {
          background-color: #342d48 !important;
        }

        /* Inline-style row highlights used on admin/list pages
           (#fff5f5 = soft red tint flagging protected/system-default rows).
           Attribute matching on the style="" string lets us override the
           inline color across 20+ admin pages without touching each file. */
        body[data-theme-version="dark"] tr[style*="#fff5f5"],
        body[data-theme-version="dark"] tr[style*="rgb(255, 245, 245)"] {
          background-color: #3a2530 !important;
        }

        /* Pale purple/pastel headers and side panels used by scheduling */
        body[data-theme-version="dark"] [style*="#faf6fe"],
        body[data-theme-version="dark"] [style*="#f8f4fe"],
        body[data-theme-version="dark"] [style*="#f0e5fa"] {
          background-color: #1e1c2e !important;
          border-color: #3a3852 !important;
          color: #d0d3e0 !important;
        }
        body[data-theme-version="dark"] [style*="#e8dff5"],
        body[data-theme-version="dark"] [style*="#e0d0f0"],
        body[data-theme-version="dark"] [style*="#c8b4e0"] {
          border-color: #3a3852 !important;
        }
        body[data-theme-version="dark"] [style*="#6a1b9a"],
        body[data-theme-version="dark"] [style*="#7a1fa2"] {
          color: #c98ce0 !important;
        }

        /* Generic table-header light grays — common across worklist/admin */
        body[data-theme-version="dark"] th[style*="#f8f9fa"],
        body[data-theme-version="dark"] td[style*="#f8f9fa"],
        body[data-theme-version="dark"] [style*="#f8f9fa"] {
          background-color: #1e1c2e !important;
          color: #d0d3e0 !important;
          border-color: #3a3852 !important;
        }
        body[data-theme-version="dark"] [style*="#fafbff"],
        body[data-theme-version="dark"] [style*="#f0f4ff"],
        body[data-theme-version="dark"] [style*="#dce8ff"] {
          background-color: #1e1c2e !important;
          border-color: #3a3852 !important;
          color: #d0d3e0 !important;
        }

        /* Inline-style "white" panels — covers fixed-position dropdowns
           that hardcode background:'#fff' inline */
        body[data-theme-version="dark"] [style*="background: #fff;"][style*="position: absolute"],
        body[data-theme-version="dark"] [style*="background: #fff;"][style*="position: fixed"],
        body[data-theme-version="dark"] [style*="background:#fff;"][style*="position:absolute"],
        body[data-theme-version="dark"] [style*="background:#fff;"][style*="position:fixed"] {
          background-color: #2a2840 !important;
          border-color: #3a3852 !important;
          color: #d0d3e0 !important;
        }

        /* Modal headers/footers that hardcode brand color backgrounds */
        body[data-theme-version="dark"] .modal-header[style*="#A336C9"],
        body[data-theme-version="dark"] .modal-header[style*="#6366F1"] {
          /* Brand backgrounds are intentional — leave color, just darken text */
          color: #fff !important;
        }
      `;
      document.head.appendChild(el);
      return () => { if (document.getElementById('praxxis-dark-form-overrides')) document.head.removeChild(el); };
    }, []);

    useEffect(() => {
		  const body = document.querySelector("body");
      let resizeWindow = () => {
        setWindowWidth(window.innerWidth);
        setWindowHeight(window.innerHeight);
        window.innerWidth <= 768
        ? body.setAttribute("data-sidebar-style", "overlay")
        : body.setAttribute("data-sidebar-style", "full");
      };
        body.setAttribute("data-typography", "poppins");
        body.setAttribute("data-theme-version", "light");
       // body.setAttribute("data-theme-version", "dark");
        body.setAttribute("data-layout", "vertical");
        body.setAttribute("data-nav-headerbg", "color_1");
        body.setAttribute("data-headerbg", "color_1");
        body.setAttribute("data-sidebar-style", "full");
        body.setAttribute("data-sibebarbg", "color_1");
        body.setAttribute("data-primary", "color_10");
        body.setAttribute("data-sidebar-position", "fixed");
        body.setAttribute("data-header-position", "fixed");
        body.setAttribute("data-container", "wide");
        body.setAttribute("direction", "ltr");
        resizeWindow();
        window.addEventListener("resize", resizeWindow);
        return () => window.removeEventListener("resize", resizeWindow);
    }, []);
    return (
    <ThemeContext.Provider
      value={{
        body,
        windowWidth,
        windowHeight,
        layout,
        themeMode,
        changeBackground,
        changeLayout,
        changeTheme,
      }}
    >
      {props.children}
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider;
